import Button from "@components/library/Button";
import { ArrowUpRightIcon } from "@components/library/Icons";
import { RadioGroup } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/outline";
import { useState } from "react";

import { classNames } from "@components/library/utils/classNames";
import { HUMANLOOP_APP_SIGNUP, SALES_CALL_LINK } from "utils/constants";

interface Product {
  name: "starter" | "team" | "enterprise";
  title: string;
  offer?: string | React.ReactNode;
  popular?: string | React.ReactNode;
  monthly_price: React.ReactNode;
  yearly_price: React.ReactNode;
  volume: string;
  usage: string;
  features: Array<string>;
  unselectable?: true;
}

export const handleBookADemo = () => {
  // Intercom may be blocked by adblockers
  if (window?.Intercom && typeof window.Intercom === "function") {
    Intercom("show");
  } else {
    window.open(SALES_CALL_LINK, "_blank");
  }
};

export const ProductSelection = () => {
  const products: Product[] = [
    {
      name: "starter",
      title: "Starter",
      monthly_price: (
        <>
          $100 / month <br />7 day free trial
        </>
      ),
      yearly_price: (
        <>
          $70 / month <br />
          annual contract <br />7 day free trial
        </>
      ),
      volume: "1k datapoints / month",
      usage: "Then $10 / 1k datapoints",
      features: [
        "1 seat",
        "Playground",
        "Logging and evaluation",
        "Manage unlimited apps",
      ],
    },
    {
      name: "team",
      title: "Team",
      popular: (
        <RadioGroup.Description
          as="span"
          className="-my-4 inline-block rounded-ms bg-blue-200 py-[4px] px-[5px] text-10-10 text-blue-700"
        >
          Most popular
        </RadioGroup.Description>
      ),
      monthly_price: (
        <>
          $1000 / month <br />7 day free trial
        </>
      ),
      yearly_price: (
        <>
          $700 / month <br />
          annual contract
          <br />7 day free trial
        </>
      ),
      volume: "100k datapoints / month",
      usage: "Then $4 / 1k datapoints",
      features: [
        "Everything from Starter",
        "Unlimited seats",
        "A/B testing",
        "Finetuning",
      ],
    },
    {
      name: "enterprise",
      title: "Enterprise",
      monthly_price: "Custom",
      yearly_price: "Custom",
      offer: (
        <div className="-mt- -ml-8 h-24">
          <Button
            styling="ghost"
            className="-mt-10 text-11-14 hover:bg-gray-200"
            // target="_blank"
            onClick={() => {
              handleSalesRequest();
            }}
            size={24}
            IconRight={ArrowUpRightIcon}
          >
            Book a call with us
          </Button>
        </div>
      ),
      volume: "Custom volume",
      usage: "Based on your needs",
      features: [
        "Everything from Team",
        "RLHF finetuning",
        "Hosted models",
        "Role-based access",
        "Dedicated support",
      ],
    },
  ];

  const [duration, setDuration] = useState<"month" | "year">("month");
  const handleSalesRequest = () => {
    // identify(llamaUser.id, { lead_type: "enterprise" });
    handleBookADemo();
  };

  return (
    <>
      <div className="-mt-24 flex w-full grow flex-col items-center gap-32">
        <MonthlyYearlyToggle duration={duration} setDuration={setDuration} />
        <RadioGroup
          value={products[1]}
          onChange={() => null}
          className="grid w-full grid-cols-1 gap-y-6 md:grid-cols-3"
        >
          {products.map((product) => (
            <RadioGroup.Option
              key={product.name}
              value={product}
              className={({ checked, active }) =>
                classNames(
                  checked
                    ? "z-10 border-blue-500 bg-blue-100"
                    : "cursor-pointer border-gray-400 ",

                  active && "border-gray-500",
                  // Neg margin to overlap the borders
                  "relative -mr-1 flex rounded border px-[20px] py-32 focus:outline-none md:rounded-none md:first:rounded-l md:last:rounded-r md:last:border-r"
                )
              }
              disabled={product.unselectable}
            >
              {({ checked, active }) => (
                <div className="flex w-full flex-col justify-between">
                  <span className="flex flex-col">
                    <div className="flex items-center gap-8">
                      <RadioGroup.Label
                        as="span"
                        className="text-14-14 font-bold text-gray-900"
                      >
                        {product.title}
                      </RadioGroup.Label>
                      {product?.popular}
                    </div>

                    <div className="min-h-[68px]">
                      <RadioGroup.Description
                        as="span"
                        className="mt-8 flex text-13-18 leading-[20px] text-gray-800"
                      >
                        {duration === "month" ? (
                          product.monthly_price
                        ) : (
                          <span className="">{product.yearly_price}</span>
                        )}
                      </RadioGroup.Description>
                      {product?.offer}
                    </div>

                    <RadioGroup.Description
                      as="span"
                      className="mt-32 mb-28 flex flex-col gap-8"
                    >
                      <div className="text-13-14 text-gray-900">
                        {product.volume}
                      </div>
                      <div className="text-13-14 text-gray-700">
                        {product.usage}
                      </div>
                    </RadioGroup.Description>
                    <RadioGroup.Description
                      as="ul"
                      className="flex flex-col gap-8 pb-8"
                    >
                      {product.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-center gap-12 text-13-16 text-gray-800"
                        >
                          <CheckCircleIcon className="inline-block h-16 w-16 text-gray-900" />
                          {feature}
                        </li>
                      ))}
                    </RadioGroup.Description>
                  </span>
                  {product.name === "enterprise" ? (
                    <Button
                      styling="solid"
                      className="mt-48 w-full"
                      size={40}
                      onClick={handleSalesRequest}
                    >
                      Book a call
                    </Button>
                  ) : (
                    <Button
                      styling="solid"
                      className="mt-48 w-full"
                      size={40}
                      href={HUMANLOOP_APP_SIGNUP}
                    >
                      Try for free
                    </Button>
                  )}
                </div>
              )}
            </RadioGroup.Option>
          ))}
        </RadioGroup>

        {/* <pre className="overflow-scroll">
          {selected.name} {duration} {llamaUser?.organizations?.[0]?.organization.id}
          <br />
          {JSON.stringify(llamaUser, null, 2)}
        </pre> */}
      </div>
    </>
  );
};

const MonthlyYearlyToggle = ({
  duration,
  setDuration,
}: {
  duration: "month" | "year";
  setDuration: (duration: "month" | "year") => void;
}) => {
  const durations = ["month", "year"];

  return (
    <RadioGroup value={duration} onChange={setDuration} className="flex">
      {durations.map((duration) => (
        <RadioGroup.Option
          key={duration}
          value={duration}
          className={({ checked, active }) =>
            classNames(
              checked
                ? "border-blue-400 bg-blue-100 text-blue-600"
                : "border-gray-400  text-gray-700 first:border-r-transparent last:border-l-transparent hover:bg-blue-100",
              active && "z-10 border-blue-400",
              "relative flex w-[154px] cursor-pointer border py-[9px] first:rounded-l last:rounded-r focus:outline-none"
            )
          }
        >
          {({ checked, active }) => (
            <RadioGroup.Label
              as="span"
              className="block w-full select-none text-center text-12-14 font-bold"
            >
              {duration === "month" && "Monthly"}
              {duration === "year" && (
                <>
                  Annually
                  <span
                    className={classNames(
                      "ml-4 rounded-full px-6 pt-2 pb-2 text-11-14",
                      checked
                        ? "bg-blue-200 text-blue-600"
                        : "bg-gray-300 text-gray-700"
                    )}
                  >
                    −30%
                  </span>
                </>
              )}
            </RadioGroup.Label>
          )}
        </RadioGroup.Option>
      ))}
    </RadioGroup>
  );
};
